import React, { FC } from 'react';
import { Box } from '@mui/material';
import styles from './styles';
import { NavyBar } from './shared';
import { SquadPortfolioBarChart } from '../SquadPortfolioBarChart';
import { SquadPortfolioPieChart } from '../SquadPortfolioPieChart';
import { SquadVectorPieChart } from '../SquadVectorPieChart';
import { SquadPortfolioTable } from '../SquadPortfolioTable';
import { SQUAD_COLORS } from '../templateConstants';
import { SQUAD_TYPOGRAPHY } from '../pageHeaderTokens';
import {
  SQUAD_PIE_BLOCK_GAP_PX,
  SQUAD_SECTION_CONTENT_GAP_PX,
  squadPieLegendAlign,
  type SquadPieLegendAlign,
} from '../squadPortfolioChartUtils';
import { squadRasterPieSlots, type SquadPortfolioBlock, type SquadPortfolioSection } from '../squadPortfolioSections';
import { squadSectionPieReserve } from '../squadDonutLabels';

/** Bild-Slot 1/2 → DOM-IDs, die die Aufnahme in PdfPreview sucht. */
const RASTER_IDS = {
  1: { id: 'squad-asset-donut', captureId: 'squad-asset-donut-wrap' },
  2: { id: 'squad-mcap-donut', captureId: 'squad-mcap-donut-wrap' },
} as const;

type Props = {
  sections: SquadPortfolioSection[];
  /** Aufgelöste Überschriften (für umgewandelte Altdokumente mit `headerKey`). */
  headers: Record<string, string>;
  primary: string;
  barColor: string;
  chartPrimary: string;
  rasterPies: boolean;
  /** `pieChartStyle` des Dokuments (Ring-Beschriftung der Vektor-Torten). */
  pieStyle?: string;
};

const kindsOf = (blocks: SquadPortfolioBlock[]) => new Set(blocks.filter((b) => b.kind !== 'empty').map((b) => b.kind));

/** Seite 2 rechts — Vorschau. Gleiche Bereichslogik wie `SquadPortfolioSectionsPdf`. */
export const SquadPortfolioSectionsPreview: FC<Props> = ({
  sections,
  headers,
  primary,
  barColor,
  chartPrimary,
  rasterPies,
  pieStyle,
}) => {
  const rasterSlots = squadRasterPieSlots(sections);

  const renderBlock = (
    block: SquadPortfolioBlock,
    slotKey: string,
    reserveTopPt?: number,
    legendAlign?: SquadPieLegendAlign,
  ) => {
    if (block.kind === 'bar') return <SquadPortfolioBarChart items={block.items} barColor={block.color || barColor} />;
    if (block.kind === 'table') return <SquadPortfolioTable rows={block.rows} header={block.header} />;
    if (block.kind === 'pie') {
      const slot = rasterPies ? rasterSlots.get(slotKey) : undefined;
      return (
        <Box sx={{ pt: `${SQUAD_PIE_BLOCK_GAP_PX}px` }}>
          {slot ? (
            <Box sx={styles.capturedChart}>
              <SquadPortfolioPieChart
                data={block.items}
                id={RASTER_IDS[slot].id}
                captureId={RASTER_IDS[slot].captureId}
                primaryColor={chartPrimary}
                colors={block.colors || []}
              />
            </Box>
          ) : (
            <SquadVectorPieChart
              data={block.items}
              primaryColor={chartPrimary}
              colors={block.colors || []}
              pieStyle={pieStyle}
              reserveTopPt={reserveTopPt}
              legendAlign={legendAlign}
            />
          )}
        </Box>
      );
    }
    return null;
  };

  return (
    <>
      {sections.map((section, si) => {
        const title = section.title || (section.headerKey ? headers[section.headerKey] : '') || '';
        const kinds = kindsOf(section.blocks);
        // Nebeneinander stehende Torten: gleiche Höhe über dem Ring, damit die Ringe fluchten.
        const pieReserve = section.columns === 2 ? squadSectionPieReserve(section.blocks, pieStyle) : undefined;
        const onlyPies = kinds.size === 1 && kinds.has('pie');
        const legendAlign = squadPieLegendAlign();
        const onlyBars = kinds.size === 1 && kinds.has('bar');
        return (
          <Box key={si} id={`squad-portfolio-section-${si}`} sx={styles.section}>
            {title ? <NavyBar color={primary}>{title}</NavyBar> : null}
            {section.blocks.length > 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  pt: `${SQUAD_SECTION_CONTENT_GAP_PX}px`,
                  flexDirection: section.columns === 2 ? 'row' : 'column',
                  ...(onlyPies || onlyBars ? {} : { alignItems: 'flex-start' }),
                }}
              >
                {section.blocks.map((block, bi) => (
                  <Box
                    key={bi}
                    sx={section.columns === 2 ? { flex: 1, minWidth: 0 } : { width: '100%', minWidth: 0 }}
                    aria-hidden={block.kind === 'empty' ? true : undefined}
                  >
                    {renderBlock(block, `${si}:${bi}`, pieReserve, legendAlign)}
                  </Box>
                ))}
              </Box>
            ) : null}
            {section.footnote ? (
              // Box statt Typography: body1 bringt responsive Schriftgrößen per Media-Query mit,
              // die das fontSize aus sx bei breitem Fenster überschreiben (Fußnote 20 statt 8 px).
              <Box
                sx={{
                  fontSize: SQUAD_TYPOGRAPHY.note,
                  color: SQUAD_COLORS.text,
                  mt: onlyPies ? 1 : 0.75,
                  whiteSpace: 'pre-line',
                  lineHeight: 1.4,
                }}
              >
                {section.footnote}
              </Box>
            ) : null}
          </Box>
        );
      })}
    </>
  );
};
